// Package imports:
import React, { useEffect, useMemo, useState } from 'react';
import { FieldArray, Form, useFormikContext } from 'formik';
// Component imports:
import FormikCheckbox from '../../../ui-elements/Input/Formik/Checkbox';
import Input from '../../../ui-elements/Input/Formik/Input';
import DropSelectSearch from '../../../ui-elements/DropSelect/Formik/DropSelectSearch';
import { DatePickerField } from '../../../ui-elements/DatePicker/Formik/DatePicker';
import Button from '../../../ui-elements/Button/Button';
import { FormObserver } from './EditContactFormObserver';
import Alert from '../../../ui-elements/Alert/Alert';
import DropSelect from '../../../ui-elements/DropSelect/DropSelect';
// util imports:
import { getTodayXYearsAgo, insertDash } from '../../../services/utils';
// Type imports:
import { ContactsOverview, EditContactFormValues, ServiceCompanyEmployee } from "../../../types/DueDiligenceTypes";
// Static data imports:
import { CountryList } from '../../../assets/staticData/CountryList';


interface IProps {
    contacts: ContactsOverview | null;
    values: EditContactFormValues;
    closeModal(): void;
    formError?: string;
    disableSubmit?: boolean;
    sendNotification?: boolean;
    employees: ServiceCompanyEmployee[];
}

export const EditContactForm: React.FC<IProps> = ({
    contacts,
    values,
    formError,
    closeModal,
    disableSubmit,
    sendNotification,
    employees
}) => {
    // Get Formik context methods to manipulate form state
    const { setFieldValue, getFieldMeta, resetForm, setValues } = useFormikContext<EditContactFormValues>();
    const countryCodeFieldMeta = getFieldMeta("country");

    // Compute the default selection for the contact dropdown on initial mount
    const contactDefaultValue = useMemo(() => {
        // If alien, always choose Other ("0")
        if (values.alien) return "0";

        // If we have an SSN, prefer matching possible contact
        if (values.ssn) {
            const found = contacts?.possibleContacts.some(c => c.ssn?.toString() === values.ssn?.toString());
            return found ? values.ssn!.toString() : "0";
        }

        // Else, fall back to current contact SSN if present, otherwise no selection
        return contacts?.currentContact?.contactSSN ?? undefined;
    }, [values.alien, values.ssn, contacts]);

    // Track the selected contact's SSN to determine which contact data to display
    const [contactSsn, setContactSsn] = useState<string | undefined>(contactDefaultValue);
    const [contactName, setContactName] = useState<string | undefined>(contacts?.currentContact?.contactName);

    // When a different contact is selected from the dropdown, auto-fill their information
    // If "Other" is selected, reset the form to empty values
    useEffect(() => {
        // Find the selected contact in the list of possible contacts
        const contact = contacts?.possibleContacts.find(contact => contact.ssn.toString() === contactSsn);

        if (contact && values.ssn !== contact.ssn) {
            // Email follows the person across the contact/procurer boundary:
            // - the new contact's typed procurer email (if any) rides up into the contact field
            // - the previous contact's email drops into their procurer slot (if they are a procurer)
            const newContactProcurer = values.procurers?.find(p => p.ssn === contact.ssn);
            const procurersWithPrevContactEmail = (values.procurers ?? []).map(p =>
                p.ssn === values.ssn ? { ...p, email: values.email } : p
            );

            // If a valid contact is found, populate the form with their data
            setValues({
                ...values,
                alien: false,
                ssn: contact.ssn,
                name: `${contact.firstname} ${contact.lastname}`,
                address: contact.address,
                areaCode: contact.areaCode,
                email: newContactProcurer?.email || contact.email || '',
                country: contact.country,
                region: contact.region,
                procurers: procurersWithPrevContactEmail,
                stopIndividualSearch: true
            })
        } else if (contactSsn === "0") {
            // When "Other" is selected, do NOT clear values. We preserve existing
            // data so that:
            // - Alien contacts keep their manual fields filled
            // - Icelandic contacts not in possibleContacts keep their SSN and name
            setValues({
                ...values,
                stopIndividualSearch: false,
                name: "",
                ssn: null,
                // Keep the previous contact's email with them if they are a procurer
                procurers: (values.procurers ?? []).map(p =>
                    p.ssn === values.ssn ? { ...p, email: values.email } : p
                )
            });
        }
    }, [contactSsn]);

    // Reset form validation state when confirmProcuration changes
    // This ensures validation errors are cleared when toggling the checkbox
    useEffect(() => {
        resetForm({ values: { ...values } });
    }, [values.confirmProcuration])


    const contactOptions = useMemo(() => {
        const data = [];
        // Track SSNs we've already added to prevent duplicates
        const addedSSNs = new Set<string>();

        // Add current contact if it exists
        if (contacts?.currentContact) {
            const ssn = contacts.currentContact.contactSSN;
            // Only add if we haven't seen this SSN before
            if (ssn && !addedSSNs.has(ssn)) {
                data.push({
                    value: ssn,
                    label: `${contacts.currentContact.contactName} ${contacts.currentContact.contactSSN
                        ? `(kt. ${insertDash(contacts.currentContact.contactSSN)})`
                        : contacts.currentContact.contactDateOfBirth
                            ? `(${contacts.currentContact.contactDateOfBirth})`
                            : ''
                        }`
                });
                addedSSNs.add(ssn);
            }
        }

        // Add possible contacts, skipping any duplicates
        if (contacts?.possibleContacts) {
            contacts.possibleContacts.forEach(contact => {
                // Only add if this SSN hasn't been added yet
                if (contact.ssn && !addedSSNs.has(contact.ssn)) {
                    data.push({
                        value: contact.ssn,
                        label: `${contact.firstname} ${contact.lastname} (kt. ${insertDash(contact.ssn)})`
                    });
                    addedSSNs.add(contact.ssn);
                }
            });
        }

        // "Annar" (Other) option - always add this at the end
        data.push({
            value: "0",
            label: "Annar"
        });

        return data;
    }, [contacts]);

    const isNotInPossibleContacts = useMemo(() => {
        //check if values.ssn is not in contacts.possibleContacts.ssn
        return values.ssn && values.ssn.length === 10 && !contacts?.possibleContacts.some(contact => contact.ssn.toString() === values.ssn);
    }, [contactSsn, contacts, values.ssn]);
    const handleChangeName = (firstname?: string, lastname?: string): string => {
        if (firstname && lastname) {
            return `${firstname} ${lastname}`;
        }
        if (firstname) {
            return firstname;
        }
        if (lastname) {
            return lastname;
        }
        return "";
    }

    // Procuration holders to list, excluding the main contact.
    // The contact's email is already captured in the field at the top, so listing
    // them again here (previously shown disabled) confused users. The original index
    // is preserved so the Formik field names stay aligned with values.procurers.
    const otherProcurers = (values.procurers ?? [])
        .map((procurer, index) => ({ procurer, index }))
        .filter(({ procurer }) => procurer.name !== contactName);

    return (
        <Form>
            <FormObserver />
            <div className="form__body">
                {/* Contact selector dropdown - shows existing contacts or "Other" option */}
                <div className="form__row">
                    <div className="form__col">
                        <DropSelect
                            clearInput={true}
                            usePortal={true}
                            label="Tengiliður"
                            options={[
                                ...contactOptions,
                            ]}
                            onChange={(value) => {
                                setContactSsn(value as string);
                                const contact = contacts?.possibleContacts.find(contact => contact.ssn.toString() === value);
                                if (contact) setContactName(handleChangeName(contact.firstname, contact.lastname));
                                else setContactName(undefined);
                            }}
                            // Use computed default: '0' for alien or unknown SSN; else matching SSN
                            defaultValue={contactDefaultValue}
                            size="lg"
                        />
                    </div>
                    <div className="form__col">
                        {/* Supervisor dropdown */}
                        <DropSelect
                            clearInput={true}
                            usePortal={true}
                            label="Umsjónaraðili (valkvætt)"
                            options={employees.map(emp => ({
                                id: String(emp.webUserId),
                                label: emp.fullname,
                                value: String(emp.webUserId)
                            }))}
                            defaultValue={values.supervisorId ? String(values.supervisorId) : null}
                            onChange={val => setFieldValue("supervisorId", val ? Number(val) : null)}
                            size="lg"
                        />
                    </div>
                </div>

                {/* Show manual entry fields when "Other" (contactSsn === "0") is selected */}
                {contactSsn === "0" && <>
                    {/* Checkbox to toggle between Icelandic (with electronic ID) and foreign contacts */}
                    <div className="form__row">
                        <div className="form__col">
                            <FormikCheckbox
                                label="Erlendur eða ekki með rafræn skilríki"
                                name="alien"
                                id="alien"
                                value={values.alien.toString()}
                                // Reset form when toggling to clear any validation errors
                                onClick={() => resetForm({ values: { ...values } })}
                            />
                        </div>
                    </div>

                    {/* Fields for Icelandic contact (alien === false) */}
                    {values.alien === false ? (
                        <>
                            <div className="form__row">
                                <div className="form__col">
                                    {/* SSN field - will auto-populate name from registry */}
                                    <Input
                                        label="Kennitala"
                                        name="ssn"
                                        id="ssn"
                                        value={values.ssn ?? ""}
                                        inputType='ssnFormat'
                                    />
                                </div>
                                <div className="form__col">
                                    {/* Name is auto-populated and disabled for Icelandic contacts */}
                                    <Input
                                        label="Nafn"
                                        name="name"
                                        id="name"
                                        value={values.name}
                                        disabled
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        // Fields for foreign contact (alien === true) - all manual entry with grid layout
                        <div className="foreign-contact-form">

                            {/* Name field */}
                            <Input label="Nafn" name="name" id="name" value={values.name} />

                            {/* Date of Birth field */}
                            <DatePickerField
                                label="Fæðingardagur"
                                name="dateOfBirth"
                                id="dateOfBirth"
                                size="lg"
                                openToDate={getTodayXYearsAgo(18)}
                                maxDate={new Date()}
                                popperPlacement="top"
                                showYearDropdown
                                showMonthDropdown
                                max_width
                            />

                            <div className="address-area">
                                {/* Address field */}
                                <Input label="Heimilisfang" name="address" id="address" value={values.address} />

                                {/* Area Code field */}
                                <Input label="Póstnúmer" name="areaCode" id="areaCode" value={values.areaCode} />
                            </div>

                            {/* Region field */}
                            <Input label="Sveitarfélag" name="region" id="region" value={values.region} />

                            {/* Country field */}
                            <DropSelectSearch
                                usePortal={true}
                                error={
                                    countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                                }
                                selectSize="lg"
                                label="Búsetuland"
                                name="country"
                                id="country"
                                options={CountryList}
                                defaultValue={values.country}
                                onSelectChange={(cc: string) => setFieldValue("country", cc)}
                            />

                            {/* Nationality field */}
                            <DropSelectSearch
                                usePortal={true}
                                error={
                                    countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                                }
                                selectSize="lg"
                                label="Ríkisfang"
                                name="nationality"
                                id="nationality"
                                options={CountryList}
                                defaultValue={values.nationality}
                                onSelectChange={(cc: string) => setFieldValue("nationality", cc)}
                            />
                        </div>
                    )}
                </>}

                {/* Email field - required for all contacts */}
                <div className="related-input">
                    <Input
                        label="Netfang"
                        name="email"
                        id="email"
                        value={values.email}
                    />
                </div>

                {/* Display form-level errors (e.g., from backend validation) */}
                {formError && (
                    <div className="form__row">
                        <div className="form__col">
                            <Alert type="error" headText={formError} />
                        </div>
                    </div>
                )}

                {
                    isNotInPossibleContacts && (
                        <div className="form__row">
                            <div className="form__col">
                                <Alert type="alert" headText={"Athugið að tengiliður hefur ekki prókúruumboð samkvæmt sóttri KYC skýrslu."} />
                            </div>
                        </div>
                    )
                }

                {/* Optional checkbox for other procuration holders to verify their identity with electronic authentication */}
                <div className="form__row">
                    <div className="form__col">
                        <FormikCheckbox
                            label="Aðrir prókúruhafar sanni á sér deili með rafrænni auðkenningu (valkvætt)"
                            name="confirmProcuration"
                            id="confirmProcuration"
                            value={values.confirmProcuration.toString()}
                        />
                    </div>
                </div>

                {/* Show other procuration holders' email fields if checkbox is checked and such procurers exist */}
                {values.confirmProcuration && otherProcurers.length > 0 ? (
                    <FieldArray name="procurationContacts">
                        {() => (
                            <>
                                {/* Render name/email fields for each procuration holder, excluding the main contact */}
                                {otherProcurers.map(({ procurer, index }) => (
                                    <div className="form__row" key={procurer.ssn || index}>
                                        <div className="form__col">
                                            {/* Name is read-only, fetched from registry */}
                                            <Input label="Nafn" name={`procurers[${index}].name`} disabled value={procurer.name} />
                                        </div>
                                        <div className="form__col">
                                            {/* Email can be edited */}
                                            <Input absoluteError label="Netfang" name={`procurers[${index}].email`} value={procurer.email} />
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </FieldArray>
                ) : values.confirmProcuration && (
                    // Show info message if confirmProcuration is checked but no other procurers exist
                    <div className="form__row">
                        <div className="form__col">
                            <Alert type="info" headText='Engir prókúruhafar skv. skýrslu' />
                        </div>
                    </div>
                )}
            </div>
            {/* Form action buttons */}
            <div className="form__actions">
                <Button
                    type="button"
                    buttonType="secondary"
                    size="lg"
                    onClick={closeModal}
                >
                    Hætta við
                </Button>
                {/* 
                    Submit button with type="submit" triggers Formik's validation and handleSubmit
                    The onClick handler was removed - all submission logic now happens in the parent's handleSubmit
                    This ensures validation runs BEFORE any API calls are made
                */}
                <Button
                    type="submit"
                    buttonType="primary"
                    size="lg"
                    disabled={disableSubmit}
                >
                    {!sendNotification ? 'Staðfesta' : 'Staðfesta og senda áreiðanleikakönnun'}
                </Button>
            </div>
        </Form>
    )
}

// Validation function that runs before form submission
// Returns an object with field names as keys and error messages as values
export const validate = (values: EditContactFormValues) => {
    const errors: Record<string, any> = {};
    // Validation for Icelandic contacts (alien === false)
    if (!values.alien) {
        if (!values.ssn) {
            errors.ssn = 'Kennitala er nauðsynleg';
        } else if (!/^\d{10}$/.test(values.ssn)) {
            errors.ssn = 'Kennitala verður að vera 10 tölustafir';
        }
        if (!values.email) errors.email = 'Netfang er nauðsynlegt';
    } else {
        // Validation for foreign contacts (alien === true) - all fields required
        if (!values.name) errors.name = 'Nafn er nauðsynlegt';
        if (!values.address) errors.address = 'Heimilisfang er nauðsynlegt';
        if (!values.areaCode) errors.areaCode = 'Póstnúmer er nauðsynlegt';
        if (!values.region) errors.region = 'Sveitarfélag er nauðsynlegt';
        if (!values.country) errors.country = 'Land er nauðsynlegt';
        if (!values.nationality) errors.nationality = 'Ríkisfang er nauðsynlegt';
        if (!values.email) errors.email = 'Netfang er nauðsynlegt';
        if (!values.dateOfBirth) errors.dateOfBirth = 'Fæðingardagur er nauðsynlegur';
    }

    // Validate procurer emails if procuration is confirmed and procurers exist
    if (values.confirmProcuration && values.procurers?.length > 0) {
        values.procurers.forEach((p, i) => {
            // Check if this procurer is the same person as the main contact
            // When they match, the email input is disabled in the UI so we skip validation
            const isMainContact = values.name && p.name === values.name;

            // Only validate email if this procurer is not the main contact
            if (!isMainContact) {
                if (!p.email) {
                    if (!errors.procurers) errors.procurers = [];
                    errors.procurers[i] = { email: 'Netfang er nauðsynlegt' };
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email)) {
                    if (!errors.procurers) errors.procurers = [];
                    errors.procurers[i] = { email: 'Ógilt netfang' };
                }
            }
        });
    }

    return errors;
};